import { useCallback, useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
import type { LocalTrackPublication, Room } from 'livekit-client';
import { useSelfieCamera } from '@/hooks/useSelfieCamera';
import { logWarn } from '@/lib/logger';

type UseTeleopSelfieParams = {
  sessionId: string;
  livekitRoomInstance: Room | null;
};

type UseTeleopSelfieResult = {
  selfieStream: MediaStream | null;
  selfieEnabled: boolean;
  selfiePreviewVisible: boolean;
  selfieSupported: boolean;
  selfieStatus: 'idle' | 'starting' | 'ready' | 'error';
  selfieError: string | null;
  selfieNotice: string | null;
  disableSelfie: () => void;
  handleSelfieToggle: () => Promise<void>;
  hideSelfiePreview: () => void;
  showSelfiePreview: () => void;
};

export const useTeleopSelfie = ({
  sessionId,
  livekitRoomInstance,
}: UseTeleopSelfieParams): UseTeleopSelfieResult => {
  const [selfieEnabled, setSelfieEnabled] = useState(false);
  const [selfiePreviewVisible, setSelfiePreviewVisible] = useState(true);
  const selfieVideoPublicationRef = useRef<LocalTrackPublication | null>(null);
  const selfieAudioPublicationRef = useRef<LocalTrackPublication | null>(null);
  const {
    stream: selfieStream,
    videoTrack: selfieVideoTrack,
    audioTrack: selfieAudioTrack,
    status: selfieStatus,
    error: selfieError,
    isSupported: selfieSupported,
    start: startSelfie,
    stop: stopSelfie,
  } = useSelfieCamera();

  const unpublishSelfie = useCallback(() => {
    const currentVideoPublication = selfieVideoPublicationRef.current;

    if (currentVideoPublication && livekitRoomInstance) {
      const currentVideoTrack = currentVideoPublication.track;

      if (currentVideoTrack) {
        livekitRoomInstance.localParticipant.unpublishTrack(currentVideoTrack);
      }
    }

    const currentAudioPublication = selfieAudioPublicationRef.current;

    if (currentAudioPublication?.track && livekitRoomInstance) {
      livekitRoomInstance.localParticipant.unpublishTrack(
        currentAudioPublication.track,
      );
    }

    selfieVideoPublicationRef.current = null;
    selfieAudioPublicationRef.current = null;
  }, [livekitRoomInstance]);

  const publishSelfie = useCallback(async () => {
    if (!livekitRoomInstance || !selfieVideoTrack || !selfieAudioTrack) {
      return;
    }

    try {
      const hasPublishedVideoTrack = selfieVideoPublicationRef.current !== null;

      if (!hasPublishedVideoTrack) {
        const videoPublication =
          await livekitRoomInstance.localParticipant.publishTrack(
            selfieVideoTrack,
            {
              name: 'selfie-video',
              source: Track.Source.Camera,
            },
          );

        selfieVideoPublicationRef.current = videoPublication;
      }

      const hasPublishedAudioTrack = selfieAudioPublicationRef.current !== null;

      if (!hasPublishedAudioTrack) {
        const audioPublication =
          await livekitRoomInstance.localParticipant.publishTrack(
            selfieAudioTrack,
            {
              name: 'selfie-audio',
              source: Track.Source.Microphone,
            },
          );

        selfieAudioPublicationRef.current = audioPublication;
      }

      livekitRoomInstance.localParticipant.setTrackSubscriptionPermissions(
        false,
        [
          {
            participantIdentity: `egress:${sessionId}`,
            allowAll: true,
          },
        ],
      );
    } catch (error) {
      logWarn('teleop.selfie.publish_failed', 'selfie publish failed', {
        session_id: sessionId,
        error: String(error),
      });
    }
  }, [livekitRoomInstance, selfieAudioTrack, selfieVideoTrack, sessionId]);

  const disableSelfie = useCallback(() => {
    setSelfieEnabled(false);
    setSelfiePreviewVisible(true);
    unpublishSelfie();
    stopSelfie();
  }, [stopSelfie, unpublishSelfie]);

  const handleSelfieToggle = useCallback(async () => {
    if (!selfieSupported) {
      return;
    }

    if (selfieEnabled) {
      disableSelfie();

      return;
    }

    setSelfiePreviewVisible(true);
    setSelfieEnabled(true);
    const selfieStartResult = await startSelfie();
    const didStartSelfie = Boolean(selfieStartResult);

    if (didStartSelfie) {
      return;
    }

    setSelfieEnabled(false);
    setSelfiePreviewVisible(true);
  }, [disableSelfie, selfieEnabled, selfieSupported, startSelfie]);

  const hideSelfiePreview = useCallback(() => {
    const isMobileLayout = window.innerWidth <= 900;

    if (isMobileLayout) {
      return;
    }

    setSelfiePreviewVisible(false);
  }, []);

  const showSelfiePreview = useCallback(() => {
    setSelfiePreviewVisible(true);
  }, []);

  useEffect(() => {
    setSelfiePreviewVisible(true);
  }, [sessionId]);

  useEffect(() => {
    const hasRoomInstance = livekitRoomInstance !== null;

    if (hasRoomInstance) {
      return;
    }

    selfieVideoPublicationRef.current = null;
    selfieAudioPublicationRef.current = null;
  }, [livekitRoomInstance]);

  useEffect(() => {
    if (!selfieEnabled) {
      unpublishSelfie();

      return;
    }

    const canPublishSelfie =
      Boolean(selfieVideoTrack) &&
      Boolean(selfieAudioTrack) &&
      Boolean(livekitRoomInstance);

    if (!canPublishSelfie) {
      return;
    }

    publishSelfie();
  }, [
    livekitRoomInstance,
    publishSelfie,
    selfieAudioTrack,
    selfieEnabled,
    selfieVideoTrack,
    unpublishSelfie,
  ]);

  useEffect(() => {
    const handleVisibility = () => {
      const documentIsHidden = document.hidden;

      if (!documentIsHidden) {
        return;
      }

      disableSelfie();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleVisibility);
    };
  }, [disableSelfie]);

  const selfieNotice = !selfieSupported
    ? 'Camera unavailable'
    : selfieError
      ? 'Camera blocked'
      : selfieEnabled && selfieStatus === 'starting'
        ? 'Starting...'
        : null;

  return {
    selfieStream,
    selfieEnabled,
    selfiePreviewVisible,
    selfieSupported,
    selfieStatus,
    selfieError,
    selfieNotice,
    disableSelfie,
    handleSelfieToggle,
    hideSelfiePreview,
    showSelfiePreview,
  };
};
